"use client";

import { setTheme } from "@/helpers";
import { BuilderBlockProps, Theme } from "@/types";
import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import styles from "./ComparisonChartTable.module.css";

type ComparisonChartContextType = {
  highlightColumnIndex?: number;
  scrollLeft?: () => void;
  scrollRight?: () => void;
  canScrollLeft?: boolean;
  canScrollRight?: boolean;
};
export const ComparisonChartContext = createContext<ComparisonChartContextType>(
  {},
);

export type ComparisonChartTableProps = {
  ariaLabel: string;
  highlightColumnIndex?: number;
  theme?: Theme;
} & BuilderBlockProps;

const ComparisonChartTable: FC<
  PropsWithChildren<ComparisonChartTableProps>
> = ({ children, ariaLabel, theme, highlightColumnIndex, attributes }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollOffset = 196;

  const updateScrollState = () => {
    if (scrollerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) {
      updateScrollState();

      scroller.addEventListener("scroll", updateScrollState);

      window.addEventListener("resize", updateScrollState);

      return () => {
        scroller.removeEventListener("scroll", updateScrollState);
        window.removeEventListener("resize", updateScrollState);
      };
    }
  }, []);

  const scrollLeft = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollBy({
        left: -scrollOffset,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollBy({
        left: scrollOffset,
        behavior: "smooth",
      });
    }
  };

  return (
    <ComparisonChartContext.Provider
      value={{
        highlightColumnIndex,
        scrollLeft,
        scrollRight,
        canScrollLeft: canScrollLeft,
        canScrollRight: canScrollRight,
      }}
    >
      <div
        ref={scrollerRef}
        {...setTheme(theme)}
        {...attributes}
        className={cn(styles.scroller, attributes?.className)}
      >
        <table
          aria-label={ariaLabel}
          className="bg-background w-max table-fixed border-separate border-spacing-x-2 pb-7 md:w-full"
        >
          <tbody>{children}</tbody>
        </table>
      </div>
    </ComparisonChartContext.Provider>
  );
};

export default ComparisonChartTable;
